import { inject, postConstruct } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { TaskModel } from '../../models/task-model';
import { TaskStatusSyncService } from '../task-status-sync-service';
import { TaskQueueService } from '../task-queue-service';
import type { BaseTaskService } from '../base-task-service';
import type { Task, TaskBasic, TaskItem } from '../../types';

export abstract class AbstractTaskServiceImpl<Params = any> implements BaseTaskService {
    @inject(TaskModel)
    private readonly _model!: TaskModel;
    @inject(TaskStatusSyncService)
    private readonly _taskStatusSyncService!: TaskStatusSyncService;
    @inject(TaskQueueService)
    private readonly _taskQueueService!: TaskQueueService;
    private _QUEUE_ID = '';

    private _onModelDataChangeEmitter = new Emitter<Task>();
    public readonly onModelDataChange = this._onModelDataChangeEmitter.event;

    @postConstruct()
    private _constructor() {
        this._QUEUE_ID = this.constructor.name;
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
        this._taskStatusSyncService.onTaskStatusChange(tasks => {
            this._updateTaskStatus(tasks);
        });
    }

    private _emitModelDataChange() {
        this._onModelDataChangeEmitter.fire(this.getModelData());
    }

    public getModelData() {
        return this._model.modelData;
    }

    private _updateModelData(value: Partial<Task>) {
        this._model.update(() => ({
            ...this.getModelData(),
            ...value,
        }));
    }

    private _updateTaskStatus(tasks: TaskItem[]) {
        const modelData = this.getModelData();

        const updatedTasks = modelData.tasks.map(task => {
            const matchedTask = tasks.find(t => t.id === task.id);

            return {
                ...task,
                status: matchedTask ? matchedTask.status : task.status,
            };
        });

        this._updateModelData({ tasks: updatedTasks });
    }

    abstract fetchTaskList(params: Params): Promise<TaskBasic>;

    public async getTaskList(params: Params) {
        this._updateModelData({ isLoading: true });

        const successCallback = (result: TaskBasic) => {
            this._taskStatusSyncService.scanTaskStatus(result.tasks);
            this._updateModelData({
                tasks: result.tasks,
                pagination: result.pagination,
                isLoading: false,
            });
        };

        const currentExecute = this._taskQueueService.pushQueue(this._QUEUE_ID,() => this.fetchTaskList(params));

        await this._taskQueueService.executeQueue<TaskBasic>(this._QUEUE_ID, currentExecute, successCallback);

        return this.getModelData();
    }
}
